import { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import ProUpsell from '@/components/ProUpsell';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Plus, Trash2, Users, Clock, PoundSterling, Download, X, Pencil, Copy } from 'lucide-react';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

const WORKER_COLORS = ['#FF5A1F', '#2563EB', '#16a34a', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#ca8a04', '#0f766e', '#4f46e5'];
// Stable colour per worker: their saved colour, else assigned by position.
const colorOf = (emp, employees) => emp?.color || WORKER_COLORS[Math.max(0, employees.findIndex((e) => e.id === emp?.id)) % WORKER_COLORS.length];

export default function TimesheetsPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const [employees, setEmployees] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showEmp, setShowEmp] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadEmployees = useCallback(() => api.get('/employees').then(({ data }) => setEmployees(data)).catch(() => {}), []);
  const loadData = useCallback(async () => {
    try {
      const [e, s] = await Promise.all([
        api.get(`/timesheets?month=${month}`),
        api.get(`/timesheets/summary?month=${month}`),
      ]);
      setEntries(e.data); setSummary(s.data);
    } catch (err) {
      if (err.response?.status !== 402) toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { if (isPro) { loadEmployees(); } else { setLoading(false); } }, [isPro, loadEmployees]);
  useEffect(() => { if (isPro) loadData(); }, [isPro, loadData]);

  if (!isPro) return <ProUpsell feature="Employee Hours" testIdSuffix="timesheets" tier="pro" />;

  const empName = (id) => employees.find((e) => e.id === id)?.name || '—';

  const exportCsv = () => {
    if (!summary?.rows?.length) { toast.error('Nothing to export'); return; }
    const head = ['Employee', 'Days', 'Normal hrs', 'Overtime hrs', 'Total hrs', 'Pay (£)'];
    const lines = summary.rows.map((r) => [r.name, r.days, r.normal_hours, r.overtime_hours, r.total_hours, r.pay].join(','));
    const t = summary.totals;
    lines.push(['TOTAL', t.days, t.normal_hours, t.overtime_hours, t.total_hours, t.pay].join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `timesheet-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Employee Hours" noindex />
      <Toaster position="top-center" richColors />
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Timesheets</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Employee Hours</h1>
          <p className="text-slate-600 mt-2">Log days &amp; hours per worker — ready for month-end wages.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="ts-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="tm-input w-auto min-h-0 py-2" />
          <button data-testid="ts-manage-employees" onClick={() => setShowEmp(true)} className="tm-btn text-xs min-h-0 py-2"><Users size={14}/> Workers ({employees.length})</button>
          <button data-testid="ts-export-csv" onClick={exportCsv} className="tm-btn tm-btn-dark text-xs min-h-0 py-2"><Download size={14}/> CSV</button>
        </div>
      </div>

      {employees.length === 0 ? (
        <div className="tm-card p-10 text-center">
          <h3 className="font-display font-bold text-xl uppercase">Add your first worker</h3>
          <p className="text-slate-600 mt-2">Add the people you employ, then log their hours below.</p>
          <button onClick={() => setShowEmp(true)} className="tm-btn mt-4"><Plus size={16}/> Add worker</button>
        </div>
      ) : (
        <>
          {/* Month summary */}
          {summary && (
            <div className="tm-card p-5 mb-6 overflow-x-auto" data-testid="ts-summary">
              <div className="flex items-center gap-2 mb-3"><Clock size={16} className="text-brand"/><h3 className="font-display font-bold uppercase">Month totals — {month}</h3></div>
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider font-semibold">
                    <th className="py-2">Worker</th><th className="text-right">Days</th><th className="text-right">Normal hrs</th><th className="text-right">Overtime hrs</th><th className="text-right">Total hrs</th><th className="text-right">Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.rows.map((r) => (
                    <tr key={r.employee_id} className="border-b border-slate-200">
                      <td className="py-2 font-medium">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorOf(employees.find((e) => e.id === r.employee_id), employees) }} />
                          {r.name}
                        </span>
                      </td>
                      <td className="text-right font-mono">{r.days}</td>
                      <td className="text-right font-mono">{r.normal_hours}</td>
                      <td className="text-right font-mono">{r.overtime_hours}</td>
                      <td className="text-right font-mono font-bold">{r.total_hours}</td>
                      <td className="text-right font-mono font-bold text-brand">{gbp(r.pay)}</td>
                    </tr>
                  ))}
                  {summary.rows.length === 0 && <tr><td colSpan="6" className="py-3 text-slate-400 font-mono">No hours logged this month.</td></tr>}
                </tbody>
                {summary.rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t border-slate-200 font-display font-semibold uppercase">
                      <td className="py-2">Total</td>
                      <td className="text-right font-mono">{summary.totals.days}</td>
                      <td className="text-right font-mono">{summary.totals.normal_hours}</td>
                      <td className="text-right font-mono">{summary.totals.overtime_hours}</td>
                      <td className="text-right font-mono">{summary.totals.total_hours}</td>
                      <td className="text-right font-mono text-brand flex items-center justify-end gap-1"><PoundSterling size={14}/>{gbp(summary.totals.pay)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          <LogEntry employees={employees} month={month} entries={entries} setMonth={setMonth} onAdded={loadData} />

          {/* Entries list */}
          <div className="tm-card p-5">
            <h3 className="font-display font-bold uppercase mb-3">Logged hours — {month}</h3>
            {loading ? <p className="font-mono text-slate-500">Loading…</p> : entries.length === 0 ? (
              <p className="text-sm text-slate-400 font-mono">No entries for this month yet.</p>
            ) : (
              <div className="divide-y divide-slate-200">
                {entries.map((en) => {
                  const net = Math.max(0, (en.hours || 0) - (en.break_minutes || 0) / 60);
                  return (
                    <div key={en.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorOf(employees.find((e) => e.id === en.employee_id), employees) }} />
                        <span className="truncate">
                          <span className="font-medium">{empName(en.employee_id)}</span>
                          <span className="text-slate-400"> · {en.date}</span>
                          {en.note && <span className="text-slate-400"> · {en.note}</span>}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 font-mono">
                        <span>{net.toFixed(2)}h{en.overtime_hours ? ` +${en.overtime_hours} OT` : ''}{en.break_minutes ? ` (−${en.break_minutes}m break)` : ''}</span>
                        <button data-testid={`ts-entry-delete-${en.id}`} onClick={async () => { try { await api.delete(`/timesheets/${en.id}`); loadData(); } catch (e) { toast.error('Could not delete'); } }} className="text-slate-400 hover:text-rose-600"><Trash2 size={14}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {showEmp && <EmployeeModal employees={employees} onClose={() => setShowEmp(false)} onChange={() => { loadEmployees(); loadData(); }} />}
    </div>
  );
}

function LogEntry({ employees, month, entries, setMonth, onAdded }) {
  const [f, setF] = useState({ employee_id: '', hours: '', overtime_hours: '', break_minutes: '', note: '' });
  // sel: { 'YYYY-MM-DD': { hours, overtime_hours, break_minutes } } — persists across month views
  const [sel, setSel] = useState({});
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const [yr, mo] = month.split('-').map(Number);
  const daysInMonth = new Date(yr, mo, 0).getDate();
  const firstOffset = (new Date(yr, mo - 1, 1).getDay() + 6) % 7; // Mon-first
  const dayStr = (d) => `${month}-${String(d).padStart(2, '0')}`;
  const isWeekend = (d) => { const wd = new Date(yr, mo - 1, d).getDay(); return wd === 0 || wd === 6; };
  const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  const fmtDay = (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

  const emp = employees.find((e) => e.id === f.employee_id);
  const selColor = emp ? colorOf(emp, employees) : '#FF5A1F';
  const defaults = () => ({
    hours: parseFloat(f.hours) || 0,
    overtime_hours: parseFloat(f.overtime_hours) || 0,
    break_minutes: parseFloat(f.break_minutes) || 0,
  });

  // Existing logged days this month, grouped for the colour overview dots.
  const dotsByDate = {};
  (entries || []).forEach((en) => {
    (dotsByDate[en.date] = dotsByDate[en.date] || new Set()).add(en.employee_id);
  });

  const toggle = (d) => setSel((prev) => {
    const k = dayStr(d);
    const next = { ...prev };
    if (next[k]) delete next[k]; else next[k] = defaults();
    return next;
  });
  const setDayField = (k, field, v) => setSel((prev) => ({ ...prev, [k]: { ...prev[k], [field]: v } }));
  const removeDay = (k) => setSel((prev) => { const n = { ...prev }; delete n[k]; return n; });
  const selectAll = () => setSel(() => {
    const n = {}; for (let i = 1; i <= daysInMonth; i++) n[dayStr(i)] = defaults(); return n;
  });
  const selectWeekdays = () => setSel(() => {
    const n = {}; for (let i = 1; i <= daysInMonth; i++) if (!isWeekend(i)) n[dayStr(i)] = defaults(); return n;
  });
  const clearDays = () => setSel({});

  const copyLastWeek = async () => {
    if (!f.employee_id) { toast.error('Pick a worker first'); return; }
    try {
      const { data } = await api.get(`/timesheets?employee_id=${f.employee_id}`);
      if (!data.length) { toast.error('No previous hours to copy for this worker'); return; }
      const latest = data.map((e) => e.date).sort().slice(-1)[0];
      const d0 = new Date(latest + 'T12:00:00');
      const dow = (d0.getDay() + 6) % 7; // 0=Mon
      const weekStart = new Date(d0); weekStart.setDate(d0.getDate() - dow);
      const weekDates = Array.from({ length: 7 }, (_, i) => { const dt = new Date(weekStart); dt.setDate(weekStart.getDate() + i); return iso(dt); });
      const byDate = {};
      data.forEach((e) => {
        if (weekDates.includes(e.date)) {
          const p = byDate[e.date] || { hours: 0, overtime_hours: 0, break_minutes: 0 };
          p.hours += e.hours || 0; p.overtime_hours += e.overtime_hours || 0; p.break_minutes += e.break_minutes || 0;
          byDate[e.date] = p;
        }
      });
      const targetStart = new Date(weekStart); targetStart.setDate(weekStart.getDate() + 7);
      const newSel = {};
      weekDates.forEach((wd, i) => {
        if (byDate[wd]) { const dt = new Date(targetStart); dt.setDate(targetStart.getDate() + i); newSel[iso(dt)] = byDate[wd]; }
      });
      if (!Object.keys(newSel).length) { toast.error('Could not find a week to copy'); return; }
      setSel(newSel);
      const tMonth = iso(targetStart).slice(0, 7);
      if (tMonth !== month) setMonth(tMonth);
      toast.success(`Copied ${Object.keys(newSel).length} days from last week — review & Log`);
    } catch (e) { toast.error('Could not copy last week'); }
  };

  const selKeys = Object.keys(sel).sort();
  const add = async () => {
    if (!f.employee_id) { toast.error('Pick a worker'); return; }
    if (selKeys.length === 0) { toast.error('Tap the days worked on the calendar'); return; }
    const hasHours = selKeys.some((k) => (sel[k].hours || 0) > 0 || (sel[k].overtime_hours || 0) > 0);
    if (!hasHours) { toast.error('Enter hours for at least one day'); return; }
    try {
      await Promise.all(selKeys.map((d) => api.post('/timesheets', {
        employee_id: f.employee_id, date: d,
        hours: parseFloat(sel[d].hours) || 0, overtime_hours: parseFloat(sel[d].overtime_hours) || 0,
        break_minutes: parseFloat(sel[d].break_minutes) || 0, note: f.note,
      })));
      setF((s) => ({ ...s, hours: '', overtime_hours: '', break_minutes: '', note: '' }));
      setSel({});
      toast.success(selKeys.length > 1 ? `Logged ${selKeys.length} days` : 'Hours logged'); onAdded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="tm-card p-5 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-display font-bold uppercase">Log hours</h3>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button data-testid="ts-copy-week" onClick={copyLastWeek} className="text-[10px] uppercase tracking-wider border border-slate-800 bg-slate-900 text-white hover:bg-slate-700 px-2 py-1 inline-flex items-center gap-1 font-semibold"><Copy size={11}/> Copy last week</button>
          <button data-testid="ts-days-weekdays" onClick={selectWeekdays} className="text-[10px] uppercase tracking-wider border border-slate-300 hover:border-slate-200 px-2 py-1 font-semibold">Weekdays</button>
          <button data-testid="ts-days-all" onClick={selectAll} className="text-[10px] uppercase tracking-wider border border-slate-300 hover:border-slate-200 px-2 py-1 font-semibold">All</button>
          <button data-testid="ts-days-clear" onClick={clearDays} className="text-[10px] uppercase tracking-wider border border-slate-300 hover:border-slate-200 px-2 py-1 font-semibold">Clear</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 mb-2">
        <select data-testid="ts-log-employee" className="tm-input bg-white" value={f.employee_id} onChange={(e) => set('employee_id', e.target.value)}>
          <option value="">Worker…</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input data-testid="ts-log-hours" type="number" step="any" className="tm-input" placeholder="Hours / day" value={f.hours} onChange={(e) => set('hours', e.target.value)} />
        <input data-testid="ts-log-overtime" type="number" step="any" className="tm-input" placeholder="Overtime / day" value={f.overtime_hours} onChange={(e) => set('overtime_hours', e.target.value)} />
        <input data-testid="ts-log-break" type="number" step="any" className="tm-input" placeholder="Break (min)" value={f.break_minutes} onChange={(e) => set('break_minutes', e.target.value)} />
        <input className="tm-input" placeholder="Note (optional)" value={f.note} onChange={(e) => set('note', e.target.value)} />
      </div>
      <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-4 font-semibold">Hours above apply to each day you tick — fine-tune individual days below</p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <div>
          <p className="tm-label text-[10px] mb-1">Days worked — {month}</p>
          <div className="grid grid-cols-7 gap-1 max-w-md" data-testid="ts-day-picker">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <div key={i} className="text-center text-[10px] uppercase text-slate-400 py-1 font-semibold">{d}</div>
            ))}
            {Array.from({ length: firstOffset }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
              const k = dayStr(d);
              const on = !!sel[k];
              const we = isWeekend(d);
              const dotEmps = Array.from(dotsByDate[k] || []);
              return (
                <button key={d} type="button" onClick={() => toggle(d)} data-testid={`ts-day-${d}`}
                  style={on ? { background: selColor, borderColor: selColor, color: '#fff' } : undefined}
                  className={`relative aspect-square flex items-center justify-center text-sm font-mono border transition-colors ${on ? 'font-bold' : we ? 'bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-400' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-200'}`}>
                  {d}
                  {!on && dotEmps.length > 0 && (
                    <span className="absolute bottom-0.5 left-0 right-0 flex items-center justify-center gap-0.5">
                      {dotEmps.slice(0, 3).map((eid) => (
                        <span key={eid} className="w-1.5 h-1.5 rounded-full" style={{ background: colorOf(employees.find((e) => e.id === eid), employees) }} />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Per-day hours */}
        <div>
          <p className="tm-label text-[10px] mb-1">Selected days {selKeys.length > 0 && `(${selKeys.length})`}</p>
          {selKeys.length === 0 ? (
            <p className="text-sm text-slate-400 font-mono py-4">Tap the days worked on the calendar. Skip days off. You can set different hours per day here.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="ts-perday-list">
              {selKeys.map((k) => (
                <div key={k} className="flex items-center gap-1.5" data-testid={`ts-perday-${k}`}>
                  <span className="w-24 shrink-0 text-xs font-mono">{fmtDay(k)}</span>
                  <input type="number" step="any" className="tm-input h-9 min-h-0 py-1 text-sm" placeholder="Hrs" value={sel[k].hours ?? ''} onChange={(e) => setDayField(k, 'hours', e.target.value)} data-testid={`ts-perday-hours-${k}`} />
                  <input type="number" step="any" className="tm-input h-9 min-h-0 py-1 text-sm" placeholder="OT" value={sel[k].overtime_hours ?? ''} onChange={(e) => setDayField(k, 'overtime_hours', e.target.value)} />
                  <input type="number" step="any" className="tm-input h-9 min-h-0 py-1 text-sm" placeholder="Brk" value={sel[k].break_minutes ?? ''} onChange={(e) => setDayField(k, 'break_minutes', e.target.value)} />
                  <button onClick={() => removeDay(k)} className="text-slate-400 hover:text-rose-600 shrink-0"><X size={14}/></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <button data-testid="ts-log-add" onClick={add} className="tm-btn text-xs min-h-0 py-2"><Plus size={14}/> Log {selKeys.length > 0 ? `${selKeys.length} day${selKeys.length > 1 ? 's' : ''}` : 'hours'}</button>
        <span className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Dots on the calendar show days already logged, coloured per worker</span>
      </div>
    </div>
  );
}

function EmployeeModal({ employees, onClose, onChange }) {
  const [f, setF] = useState({ name: '', pay_rate: '', overtime_rate: '', color: WORKER_COLORS[0] });
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error('Name required'); return; }
    const body = { name: f.name.trim(), pay_rate: parseFloat(f.pay_rate) || 0, overtime_rate: parseFloat(f.overtime_rate) || 0, color: f.color || '', active: true };
    try {
      if (editId) await api.put(`/employees/${editId}`, body);
      else await api.post('/employees', body);
      setF({ name: '', pay_rate: '', overtime_rate: '', color: WORKER_COLORS[employees.length % WORKER_COLORS.length] }); setEditId(null);
      toast.success('Saved'); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (emp) => { setEditId(emp.id); setF({ name: emp.name, pay_rate: emp.pay_rate || '', overtime_rate: emp.overtime_rate || '', color: colorOf(emp, employees) }); };
  const del = async (id) => {
    if (!window.confirm('Delete this worker and all their logged hours?')) return;
    try { await api.delete(`/employees/${id}`); onChange(); } catch (e) { toast.error('Could not delete'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="tm-card bg-white p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ts-employee-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl uppercase">Workers</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={20}/></button>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-2">
          <input data-testid="ts-emp-name" className="tm-input col-span-3" placeholder="Worker name" value={f.name} onChange={(e) => set('name', e.target.value)} />
          <label className="block"><span className="tm-label">Pay £/hr</span>
            <input data-testid="ts-emp-rate" type="number" step="any" className="tm-input" placeholder="18" value={f.pay_rate} onChange={(e) => set('pay_rate', e.target.value)} /></label>
          <label className="block"><span className="tm-label">Overtime £/hr</span>
            <input type="number" step="any" className="tm-input" placeholder="27" value={f.overtime_rate} onChange={(e) => set('overtime_rate', e.target.value)} /></label>
          <div className="flex items-end"><button data-testid="ts-emp-save" onClick={save} className="tm-btn w-full text-xs min-h-0 py-2">{editId ? 'Update' : 'Add'}</button></div>
        </div>
        <div className="mb-3">
          <span className="tm-label">Calendar colour</span>
          <div className="flex flex-wrap gap-1.5" data-testid="ts-emp-colors">
            {WORKER_COLORS.map((c) => (
              <button key={c} type="button" onClick={() => set('color', c)} data-testid={`ts-emp-color-${c.replace('#', '')}`}
                style={{ background: c }}
                className={`w-7 h-7 rounded-full border transition-transform ${f.color === c ? 'border-slate-200 scale-110' : 'border-transparent'}`} />
            ))}
          </div>
        </div>
        <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-3 font-semibold">Leave overtime blank to use the normal rate</p>
        <div className="divide-y divide-slate-200">
          {employees.map((e) => (
            <div key={e.id} className="py-2 flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colorOf(e, employees) }} />
                <span><span className="font-medium">{e.name}</span><span className="text-slate-400 block text-xs">{gbp(e.pay_rate)}/hr{e.overtime_rate ? ` · OT ${gbp(e.overtime_rate)}/hr` : ''}</span></span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => edit(e)} className="text-slate-400 hover:text-slate-900"><Pencil size={14}/></button>
                <button data-testid={`ts-emp-delete-${e.id}`} onClick={() => del(e.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {employees.length === 0 && <p className="text-sm text-slate-400 font-mono py-2">No workers yet.</p>}
        </div>
      </div>
    </div>
  );
}
